import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, type SyncPlan, type SyncResult, type SyncStatus } from '../api';
import { canWrite, isOwner, useAuth } from '../auth';
import { ItemImage } from '../components/ItemImage';
import { Badge, Button, Card, Checkbox, Empty, ErrorMessage, Notice, PageHeader, Spinner, number, plural, useConfirm, when } from '../ui';

const KIND = { create: { label: 'Add to BrickLink', tone: 'green' }, update: { label: 'Change on BrickLink', tone: 'blue' }, zero: { label: 'Set to zero', tone: 'amber' } } as const;

/** Sending lots to BrickLink. Check first (a dry run), then send one lot to try it, then the rest. */
export function Sync() {
  const { user } = useAuth();
  const owner = isOwner(user);
  const writable = canWrite(user);
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [includeZero, setIncludeZero] = useState(false);
  const [lastResult, setLastResult] = useState<SyncResult | null>(null);

  const status = useQuery({ queryKey: ['sync-status'], queryFn: () => api<SyncStatus>('/api/v1/bricklink/sync/status') });
  const plan = useMutation({
    mutationFn: () => api<{ plan: SyncPlan }>('/api/v1/bricklink/sync/plan', { body: { refresh: true, includeZero } }).then((r) => r.plan),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['sync-status'] }),
  });
  const [down, setDown] = useState<Set<number>>(new Set());
  const apply = useMutation({
    mutationFn: (v: { lotIds?: number[]; takeDown?: boolean }) =>
      api<{ result: SyncResult }>('/api/v1/bricklink/sync/apply', { body: { ...(v.lotIds ? { lotIds: v.lotIds } : {}), includeZero, ...(v.takeDown ? { takeDown: true } : {}) } }).then((r) => r.result),
    onSuccess: (r) => {
      setLastResult(r);
      setDown(new Set());
      plan.mutate(); // read BrickLink again so the list shows what is left
    },
  });
  // Draft lots that could go Live: they have stock and a price. A dry run counts them without changing anything.
  const readyBody = { target: { filter: { status: 'draft', qtyMin: 1, priceMin: 0.0001 } }, action: { type: 'set_status', status: 'live' } };
  const ready = useQuery({
    queryKey: ['draft-ready', status.data?.lotsByStatus.draft ?? 0],
    enabled: !!status.data,
    queryFn: () => api<{ result: { changed: number } }>('/api/v1/lots/bulk', { body: { ...readyBody, dryRun: true } }).then((r) => r.result.changed),
  });
  const goLive = useMutation({
    mutationFn: () => api('/api/v1/lots/bulk', { body: { ...readyBody, dryRun: false } }),
    onSuccess: () => {
      for (const k of ['sync-status', 'draft-ready', 'lots', 'dashboard']) void qc.invalidateQueries({ queryKey: [k] });
    },
  });
  const writes = useMutation({
    mutationFn: (on: boolean) => api('/api/v1/channels/bricklink', { method: 'PUT', body: on ? { mode: 'read_write', confirmWrites: true } : { mode: 'read_only' } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['sync-status'] }),
  });

  const s = status.data;
  const p = plan.data;
  const live = s?.lotsByStatus.live ?? 0;
  const stockroom = s?.lotsByStatus.stockroom ?? 0;
  const draft = s?.lotsByStatus.draft ?? 0;

  const turnOn = async () => {
    if (await confirm('This lets BrickLoop create and change lots in your real BrickLink store, only from this page and only when you press Send. It never deletes a lot. You can switch it off again here at any time. Switch it on?', { label: 'Allow it' })) writes.mutate(true);
  };
  const sendAll = async () => {
    if (!p) return;
    const n = Math.min(p.actions.length, 100);
    if (await confirm(`Send ${plural(n, 'change')} to your BrickLink store now (${p.summary.create} to add, ${p.summary.update} to change${includeZero ? `, ${p.summary.zero} to set to zero` : ''})? If BrickLink's answer for any lot does not match what was sent, it stops there.`, { label: `Send ${n}` })) apply.mutate({});
  };
  const sendOne = async (lotId: number, label: string) => {
    if (await confirm(`Send ${label} to your BrickLink store now?`, { label: 'Send it' })) apply.mutate({ lotIds: [lotId] });
  };

  return (
    <>
      {dialog}
      <PageHeader title="Sync to BrickLink" subtitle="Send your Live and Stockroom lots to your BrickLink store. Nothing is sent until you press Send." />
      <div className="space-y-4">
        {status.isPending && <Spinner />}
        {s && (
          <Card title="What gets sent">
            <ul className="space-y-1 text-sm text-slate-700">
              <li>
                <strong>{number(live)}</strong> local lots are marked Live, <strong>{number(stockroom)}</strong> are marked Stockroom, and <strong>{number(draft)}</strong> are Drafts. The check below shows how many actually match BrickLink.
              </li>
              <li>
                <strong>Only Live and Stockroom lots are sent</strong>, and only if they have stock and a price. Drafts are never sent. Put lots live from the lot page, or in bulk from the{' '}
                <Link to="/items?view=lots" className="underline">
                  Inventory
                </Link>{' '}
                list.
              </li>
              <li>Making a lot <strong>Draft</strong> only stops it being sent. It does <strong>not</strong> remove it from BrickLink: a lot already there stays for sale at its last quantity until you change it on BrickLink.</li>
              <li>Sent: quantity, price, condition, stockroom or not, bulk, and your remarks and description when you have written them. BrickLoop never deletes a BrickLink lot.</li>
            </ul>
            {writable && (ready.data ?? 0) > 0 && (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded border border-slate-200 p-3 text-sm">
                <span>
                  <strong>{number(ready.data ?? 0)}</strong> Draft lots have stock and a price. Putting them Live only changes their status here: nothing is sent to BrickLink until you check and send below.
                </span>
                <Button
                  variant="secondary"
                  small
                  disabled={goLive.isPending}
                  onClick={async () => {
                    if (await confirm(`Make ${plural(ready.data ?? 0, 'Draft lot')} with stock and a price Live? You can change any back to Draft afterwards. Nothing is sent to BrickLink by this.`, { label: 'Make them Live' })) goLive.mutate();
                  }}
                >
                  {goLive.isPending ? 'Working…' : `Make ${number(ready.data ?? 0)} Live…`}
                </Button>
              </div>
            )}
            <ErrorMessage error={goLive.error} />
            <div className="mt-3 flex flex-wrap items-center gap-3 rounded bg-slate-50 p-3 text-sm">
              <span>
                Writing to BrickLink is{' '}
                {s.writesOn ? <Badge tone="amber">ON</Badge> : <Badge>OFF</Badge>}
                {s.lastPushAt && (
                  <span className="ml-2 text-xs text-slate-600">
                    Last sent {when(s.lastPushAt)}
                    {s.lastPushStatus ? ` (${s.lastPushStatus})` : ''}
                  </span>
                )}
              </span>
              {owner ? (
                s.writesOn ? (
                  <Button variant="secondary" small disabled={writes.isPending} onClick={() => writes.mutate(false)}>
                    Switch writing off
                  </Button>
                ) : (
                  <Button variant="secondary" small disabled={writes.isPending || !s.connected} onClick={turnOn} title={!s.connected ? 'Connect BrickLink from Channels first' : undefined}>
                    Allow BrickLoop to change my store…
                  </Button>
                )
              ) : (
                <span className="text-xs text-slate-500">Only the owner can switch writing on.</span>
              )}
            </div>
            <ErrorMessage error={writes.error} />
          </Card>
        )}

        <Card title="1. Check what would change">
          <p className="mb-3 text-sm text-slate-600">Reads your BrickLink inventory (one read-only request) and compares it with your Live and Stockroom lots. This only looks: nothing is sent.</p>
          <div className="flex flex-wrap items-center gap-3">
            <Button disabled={plan.isPending || !s?.connected} onClick={() => plan.mutate()}>
              {plan.isPending ? 'Reading BrickLink…' : 'Check what would change'}
            </Button>
            <Checkbox label="Also set lots that are sold out here, but still listed on BrickLink, to zero" checked={includeZero} onChange={setIncludeZero} />
          </div>
          {s?.connected === false && <p className="mt-2 text-sm text-amber-700">BrickLink is not connected. Turn it on from Channels first.</p>}
          <ErrorMessage error={plan.error} />
        </Card>

        {lastResult && (
          <Notice tone={lastResult.stoppedBecause ? 'amber' : 'green'}>
            Sent {lastResult.succeeded} of {lastResult.attempted}.{lastResult.remaining > 0 && ` ${plural(lastResult.remaining, 'change')} left.`}
            {lastResult.stoppedBecause && ` ${lastResult.stoppedBecause}`}
            {lastResult.unverified.length > 0 && (
              <div className="text-xs">
                BrickLink did not say whether {lastResult.unverified.join(', ')} was right, so that could not be checked.
              </div>
            )}
            {lastResult.items.filter((i) => !i.ok).slice(0, 3).map((i) => (
              <div key={i.lotId} className="text-xs">
                {i.itemNo} {i.colorName}: {i.message}
              </div>
            ))}
          </Notice>
        )}
        <ErrorMessage error={apply.error} />

        {p && (
          <Card
            title="2. What would be sent"
            actions={
              owner && s?.writesOn && p.actions.length > 0 ? (
                <Button disabled={apply.isPending} onClick={sendAll}>
                  {apply.isPending ? 'Sending…' : `Send ${Math.min(p.actions.length, 100)}`}
                </Button>
              ) : undefined
            }
          >
            <p className="mb-3 text-sm text-slate-600">
              {plural(p.summary.wanted, 'lot')} {p.summary.wanted === 1 ? 'is' : 'are'} meant to be for sale. {number(p.summary.inSync)} already match BrickLink.
              {p.summary.draftOnBrickLink > 0 && ` ${plural(p.summary.draftOnBrickLink, 'Draft lot')} ${p.summary.draftOnBrickLink === 1 ? 'is' : 'are'} on BrickLink but left alone.`}
              {p.summary.onlyOnBrickLink > 0 && ` ${plural(p.summary.onlyOnBrickLink, 'BrickLink lot')} ${p.summary.onlyOnBrickLink === 1 ? 'has' : 'have'} no lot here and ${p.summary.onlyOnBrickLink === 1 ? 'is' : 'are'} left alone.`}
              {' '}BrickLink was read {when(p.listingsReadAt)}.
            </p>
            {!s?.writesOn && p.actions.length > 0 && <Notice tone="amber">Writing is off, so these cannot be sent yet. Switch it on above when you are ready. Try one lot first.</Notice>}
            {p.actions.length === 0 && <Empty>{p.blocked.length > 0 ? 'No changes can be sent until the blocked lots below are resolved.' : 'Nothing to send: what is Live here already matches BrickLink.'}</Empty>}
            {p.actions.length > 0 && (
              <div className="mt-3 overflow-x-auto">
                <table className="data w-full min-w-[44rem] text-sm">
                  <thead>
                    <tr>
                      <th className="min-w-14" />
                      <th>Item</th>
                      <th>Action</th>
                      <th>What changes</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {p.actions.slice(0, 200).map((a) => (
                      <tr key={a.lotId}>
                        <td className="min-w-14">
                          <ItemImage itemType={a.itemType} itemNo={a.itemNo} colorId={a.colorId} />
                        </td>
                        <td>
                          <Link to={`/lots/${a.lotId}`} className="font-medium hover:underline">
                            {a.itemNo}
                          </Link>{' '}
                          <span className="text-slate-600">
                            {a.colorName} · {a.condition === 'N' ? 'New' : 'Used'}
                          </span>
                          <div className="max-w-64 truncate text-xs text-slate-500">{a.itemName}</div>
                        </td>
                        <td>
                          <Badge tone={KIND[a.kind].tone}>{KIND[a.kind].label}</Badge>
                        </td>
                        <td className="text-xs">
                          {a.changes.map((c) => (
                            <div key={c.field}>
                              <span className="text-slate-500">{c.field}:</span> {c.from ?? '—'} → <strong>{c.to ?? '—'}</strong>
                            </div>
                          ))}
                        </td>
                        <td className="text-right">
                          {owner && s?.writesOn && writable && (
                            <Button variant="secondary" small disabled={apply.isPending} onClick={() => sendOne(a.lotId, `${a.itemNo} ${a.colorName}`)}>
                              Send just this
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {p.actions.length > 200 && <p className="mt-2 text-xs text-slate-500">Showing the first 200 of {number(p.actions.length)}.</p>}
              </div>
            )}
            {p.blocked.length > 0 && (
              <details open className="mt-4 text-sm">
                <summary className="cursor-pointer font-medium">{plural(p.blocked.length, 'lot')} cannot be sent</summary>
                <ul className="mt-2 space-y-1 text-slate-700">
                  {p.blocked.slice(0, 100).map((b) => (
                    <li key={b.lotId}>
                      <Link to={`/lots/${b.lotId}`} className="font-medium hover:underline">
                        {b.itemNo}
                      </Link>{' '}
                      {b.colorName} · {b.condition === 'N' ? 'New' : 'Used'}: {b.reason}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {owner && p.blocked.some((b) => b.manualCompleteness) && (
              <Notice tone="amber">
                Completeness changes need a manual update on BrickLink.{' '}
                <a className="underline" href="/api/v1/bricklink/sync/completeness.xml">Download completeness XML</a>, then upload it on{' '}
                <a className="underline" href="https://www.bricklink.com/invXML.asp" target="_blank" rel="noreferrer">BrickLink’s Mass Inventory Update page</a>.
                {' '}The file changes only completeness on the existing listings. Afterwards, press Check what would change again.
              </Notice>
            )}
          </Card>
        )}
        {p && p.offSale.length > 0 && (
          <Card title="3. Still for sale on BrickLink, but not Live here">
            <p className="mb-3 text-sm text-slate-600">
              These lots are on BrickLink with stock, but are Draft, sold out or empty here, so BrickLoop does not send them. They are <strong>still for sale on BrickLink</strong>. You can take chosen ones down: they are set to quantity 0 and kept on BrickLink (nothing is deleted), and you can set a quantity again later. If a lot is only Draft because you have not got round to it, leave it.
            </p>
            {owner && s?.writesOn && (
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Button
                  variant="secondary"
                  small
                  disabled={down.size === 0 || apply.isPending}
                  onClick={async () => {
                    if (await confirm(`Take ${plural(down.size, 'lot')} down from your BrickLink store (set to quantity 0 and kept)? Anyone browsing your store will no longer see ${down.size === 1 ? 'it' : 'them'} for sale.`, { label: 'Take them down', danger: true })) apply.mutate({ lotIds: [...down], takeDown: true });
                  }}
                >
                  Take down {down.size > 0 ? down.size : 'chosen'}…
                </Button>
                <Button variant="ghost" small onClick={() => setDown(new Set(p.offSale.slice(0, 100).map((a) => a.lotId)))}>
                  Choose all shown
                </Button>
                <Button variant="ghost" small onClick={() => setDown(new Set())}>
                  Clear
                </Button>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="data w-full min-w-[40rem] text-sm">
                <thead>
                  <tr>
                    {owner && s?.writesOn && <th className="w-8" />}
                    <th className="min-w-14" />
                    <th>Item</th>
                    <th className="text-right">On BrickLink</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {p.offSale.slice(0, 200).map((a) => (
                    <tr key={a.lotId}>
                      {owner && s?.writesOn && (
                        <td>
                          <input
                            type="checkbox"
                            className="size-4"
                            aria-label={`Choose ${a.itemNo} ${a.colorName}`}
                            checked={down.has(a.lotId)}
                            onChange={(e) =>
                              setDown((d) => {
                                const next = new Set(d);
                                if (e.target.checked) next.add(a.lotId);
                                else next.delete(a.lotId);
                                return next;
                              })
                            }
                          />
                        </td>
                      )}
                      <td className="min-w-14">
                        <ItemImage itemType={a.itemType} itemNo={a.itemNo} colorId={a.colorId} />
                      </td>
                      <td>
                        <Link to={`/lots/${a.lotId}`} className="font-medium hover:underline">
                          {a.itemNo}
                        </Link>{' '}
                        <span className="text-slate-600">
                          {a.colorName} · {a.condition === 'N' ? 'New' : 'Used'}
                        </span>
                        <div className="max-w-64 truncate text-xs text-slate-500">{a.itemName}</div>
                      </td>
                      <td className="text-right tabular-nums">{a.changes[0]?.from} for sale</td>
                      <td className="text-right">
                        {owner && s?.writesOn && writable && (
                          <Button
                            variant="secondary"
                            small
                            disabled={apply.isPending}
                            onClick={async () => {
                              if (await confirm(`Take ${a.itemNo} ${a.colorName} down from your BrickLink store (set to quantity 0 and kept)?`, { label: 'Take it down', danger: true })) apply.mutate({ lotIds: [a.lotId], takeDown: true });
                            }}
                          >
                            Take down
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {p.offSale.length > 200 && <p className="mt-2 text-xs text-slate-500">Showing the first 200 of {number(p.offSale.length)}.</p>}
          </Card>
        )}

      </div>
    </>
  );
}
